/**
 * printerSettings — ค่าเครื่องพิมพ์ของ "เครื่องนี้" (เบราว์เซอร์นี้) ไม่ใช่ของสาขา
 *
 * หลักแบ่ง: ฮาร์ดแวร์ตั้งที่เครื่อง · เนื้อหาใบเสร็จ/นโยบายตั้งที่สาขา
 * (docs/thermal-printer-setup.md §Device printer settings)
 *  - transport   'usb' | 'bluetooth' | 'none'  — 'none' = ไม่มีเครื่องพิมพ์ต่อตรง ใบเสร็จออกเป็น PDF
 *  - paperWidth  '80mm' | '58mm' — ม้วนกระดาษของเครื่องพิมพ์ที่ต่ออยู่
 *  - btSpeed     'normal' | 'fast' — ความเร็วส่งผ่าน Bluetooth (BT_SPEEDS ใน bluetoothPrinter.js)
 *                เครื่องพิมพ์แต่ละรุ่นรับได้ไม่เท่ากัน จึงให้เลือกเองหลังพิมพ์ทดสอบ
 *  - cashDrawer  ลิ้นชักเสียบสายอยู่กับเครื่องพิมพ์นี้ไหม — สวิตช์เดียวคุมทั้งการเตะลิ้นชัก
 *                ตอนรับเงินสด และปุ่ม "เปิดลิ้นชัก" บนหน้า POS
 *
 * เคยเป็นคอลัมน์ของสาขา (bill_paper_width / open_cash_drawer_on_cash) แต่สาขาเดียว
 * มีได้ทั้งเครื่อง 80mm ที่เคาน์เตอร์และเครื่องพกพา 58mm ที่ไม่มีลิ้นชัก ค่าเดียวผิดทันที
 * ส่วน auto_print_bill ยังอยู่ที่สาขา เพราะเป็นนโยบายของเจ้าของร้าน ไม่ใช่ฮาร์ดแวร์
 *
 * เก็บใน localStorage เพราะสิทธิ์จับคู่เครื่องพิมพ์ของ Chrome ก็ผูกกับเบราว์เซอร์อยู่แล้ว
 * (ล้างข้อมูลเบราว์เซอร์ = ต้องจับคู่ใหม่อยู่ดี) — ไม่ลง pos_devices เพราะทะเบียนเครื่องเป็น optional
 *
 * ไฟล์นี้ต้องเบา ห้าม import escposPrinter/pdfmake — main.js กับ POSLayout โหลดตรง
 */
import { reactive, readonly } from 'vue';

const STORAGE_KEY = 'printer.settings';

export const TRANSPORTS = ['usb', 'bluetooth', 'none'];
export const PAPER_WIDTHS = ['80mm', '58mm'];
// ไม่มีระดับ "ส่งรัวไม่เว้นจังหวะ" — พิมพ์ใบทดสอบผ่านแต่ใบเสร็จจริง (มี QR) ค้าง ดู BT_SPEEDS
// ค่าเก่า 'max' จากช่วงทดลองตกเป็นค่าเริ่มต้น ('fast') ผ่าน normalize()
export const BT_SPEED_KEYS = ['normal', 'fast'];

// btSpeed เริ่มที่ 'fast' — ยืนยันบนเครื่องจริง 2026-09-30 (เครื่องพิมพ์ของ Mond + Mac Chrome)
// ทั้งบิลทดสอบยาวแบบมีบล็อกดำและใบเสร็จจริงที่มี QR · 'normal' ช้ากว่าราว 2 เท่า
// เครื่องพิมพ์ที่รับก้อน 512 ไบต์ไม่ได้ (MTU เล็ก) จะพิมพ์เพี้ยน → ให้ลดเป็น 'normal' ในหน้าตั้งค่า
const DEFAULTS = { transport: 'none', paperWidth: '80mm', cashDrawer: false, btSpeed: 'fast' };

// reactive (ไม่ใช่ object ธรรมดา) เพื่อให้ computed ที่อ่านค่า — ปุ่มพิมพ์/ปุ่มเปิดลิ้นชัก —
// อัปเดตเองทันทีที่ตั้งค่าเสร็จ โดยไม่ต้อง reload หน้า
const state = reactive({ configured: false, ...DEFAULTS });

function normalize(raw) {
  return {
    transport: TRANSPORTS.includes(raw?.transport) ? raw.transport : DEFAULTS.transport,
    paperWidth: PAPER_WIDTHS.includes(raw?.paperWidth) ? raw.paperWidth : DEFAULTS.paperWidth,
    cashDrawer: raw?.cashDrawer === true,
    btSpeed: BT_SPEED_KEYS.includes(raw?.btSpeed) ? raw.btSpeed : DEFAULTS.btSpeed,
  };
}

function load() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (raw) Object.assign(state, normalize(raw), { configured: true });
  } catch (e) { /* ไม่มี/อ่านไม่ได้ = ยังไม่ได้ตั้ง */ }
}
load();

export const printerSettings = readonly(state);

export function savePrinterSettings(patch) {
  Object.assign(state, normalize({ ...state, ...patch }), { configured: true });
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalize(state)));
  } catch (e) { /* โหมดส่วนตัว/พื้นที่เต็ม — ใช้ได้จนปิดแท็บ */ }
}

/**
 * เดาขนาดกระดาษจากชื่อเครื่องพิมพ์ — ยี่ห้อทั่วไปใส่ขนาดไว้ในชื่อรุ่น
 * ("POS80 Printer USB" = NEO 8300, "POS58", "XP-58IIH", "POS-5890K", "XP-80C")
 * จับเฉพาะเลขที่ยืนเดี่ยว (ไม่ติดเลขอื่น) กัน "N160" / "T82" / "8300" หลอก
 * 58xx นับเป็น 58mm (ตระกูล 5890/5805 เป็น 58mm ทั้งหมด) · 72 = พื้นที่พิมพ์ของ 80mm
 * ไม่เจอ = null → คงค่าเดิมไว้ ให้คนเลือกเอง
 */
export function guessPaperWidth(name) {
  const s = String(name || '');
  if (/(^|\D)58(\d{2})?(?!\d)/.test(s)) return '58mm';
  if (/(^|\D)(80|72)(?!\d)/.test(s)) return '80mm';
  return null;
}

/** ขนาดกระดาษที่ใช้จัดหน้าใบเสร็จ/สลิป — ใช้ทั้งทางพิมพ์ตรงและทาง PDF */
export function printerPaperWidth() {
  return state.paperWidth;
}

/** เครื่องนี้มีลิ้นชักให้เตะไหม — ต้องมีเครื่องพิมพ์ต่อตรงด้วย เพราะลิ้นชักเสียบอยู่กับมัน */
export function hasCashDrawer() {
  return state.configured && state.transport !== 'none' && state.cashDrawer;
}

// ---- ย้ายค่าจากสาขา (ใช้ครั้งเดียวหลังอัปเดต) --------------------------------

let resolveReady;
/** resolve เมื่อเช็คการย้ายค่าเสร็จ — POSLayout รอก่อนตัดสินว่าจะถามให้ตั้งค่าไหม */
export const printerSettingsReady = new Promise(resolve => { resolveReady = resolve; });

/**
 * เรียกครั้งเดียวตอนบูตแอป (main.js)
 *
 * แคชเชียร์ที่พิมพ์ผ่าน USB ได้อยู่ก่อนอัปเดตต้องพิมพ์ต่อได้ทันที ไม่ใช่เจอหน้าต่างให้ตั้งค่า
 * กลางกะ → ถ้ายังไม่เคยตั้งและ Chrome มีสิทธิ์เครื่องพิมพ์ USB อยู่แล้ว ให้ตั้งเป็น usb เอง
 * ขนาดกระดาษเอาจากค่าสาขาที่ shop store แคชไว้ตอนเข้าร้านครั้งล่าสุด ลิ้นชัก = เปิด
 * (ค่า default ของสาขาเดิม และถ้าไม่มีลิ้นชักจริง คำสั่งเตะก็ไม่มีผลอะไร)
 *
 * สัญญาณว่า "เคยพิมพ์ USB จากเบราว์เซอร์นี้" มี 3 ทาง เพราะ getDevices() ไม่คืนเครื่องที่
 * ถอดสาย/ปิดอยู่ — เครื่องพิมพ์ปิดตอนเปิดแอปต้องไม่ทำให้แคชเชียร์เดิมหลุดไปทาง PDF:
 *  1. getDevices() มีเครื่อง (เสียบอยู่ตอนนี้)
 *  2. มี serial ที่ escposPrinter เคยจำไว้ (เคยเลือกเครื่องมาแล้ว — POS-80 บางรุ่นไม่ส่ง serial
 *     จึงใช้ข้อนี้อย่างเดียวไม่ได้)
 *  3. เสียบเครื่องที่เคยให้สิทธิ์ไว้ทีหลัง (event connect ยิงเฉพาะเครื่องที่ origin นี้มีสิทธิ์)
 */
const LEGACY_SERIAL_KEY = 'escpos_printer_serial'; // เจ้าของคือ escposPrinter.js

/**
 * เบราว์เซอร์นี้พิมพ์ตรงได้ทางใดทางหนึ่งไหม (WebUSB หรือ Web Bluetooth) — เช็คจาก API ที่มีจริง
 * Safari/Chrome บน iPhone/iPad = ไม่ได้ (iOS บังคับ WebKit ซึ่งไม่มีทั้งสองอย่าง) · Firefox/Safari
 * บน Mac ก็ไม่ได้ · แต่แอปเบราว์เซอร์บน iOS ที่ใส่ Web Bluetooth มาเอง (Bluefy) = ได้ ต้องไม่บล็อก
 */
export function canDirectPrint() {
  if (typeof navigator === 'undefined') return false;
  return !!(navigator.usb || navigator.bluetooth);
}

export async function initPrinterSettings({ legacyPaperWidth } = {}) {
  // พิมพ์ตรงไม่ได้เลย = คำตอบเดียวที่เป็นไปได้คือ "ไม่มีเครื่องพิมพ์" (ใบเสร็จเป็น PDF → AirPrint/แชร์)
  // ตั้งให้เลย ไม่ต้องถามตอนเข้า POS และไอคอนไม่ต้องขึ้นจุดเหลืองค้างตลอดไป
  if (!state.configured && !canDirectPrint()) {
    savePrinterSettings({ transport: 'none' });
    resolveReady();
    return;
  }
  const migrate = () => {
    if (state.configured) return;
    savePrinterSettings({
      transport: 'usb',
      paperWidth: legacyPaperWidth === '58mm' ? '58mm' : '80mm',
      cashDrawer: true,
    });
  };
  try {
    if (!state.configured && typeof navigator !== 'undefined' && navigator.usb) {
      let hasSerial = false;
      try { hasSerial = !!localStorage.getItem(LEGACY_SERIAL_KEY); } catch (e) { /* ignore */ }
      const granted = hasSerial ? [] : await navigator.usb.getDevices();
      if (hasSerial || (granted && granted.length)) {
        migrate();
      } else {
        const onConnect = () => {
          navigator.usb.removeEventListener('connect', onConnect);
          migrate();
        };
        navigator.usb.addEventListener('connect', onConnect);
      }
    }
  } catch (e) {
    // ไม่มีสิทธิ์/เบราว์เซอร์ไม่ยอม — ปล่อยให้หน้าต่างถามตั้งค่าจัดการ
  } finally {
    resolveReady();
  }
}

// ---- offcanvas ตั้งค่า (เปิดได้จากหลายที่) ----------------------------------

// ไอคอนที่แถบบน POS/back-office, การ์ดในหน้าตั้งค่า และหน้าต่างถามครั้งแรก เปิด offcanvas
// ตัวเดียวกัน — layout เป็นคน mount (v-if ตามค่านี้) คนกดแค่สั่งเปิด
export const printerSettingsUi = reactive({ open: false });

export function openPrinterSettings() {
  printerSettingsUi.open = true;
}

// ---- หน้าต่างถามตั้งค่าครั้งแรก ---------------------------------------------

const PROMPT_SKIP_KEY = 'printer.setup_skipped';

/** กด "ไว้ทีหลัง" แล้ว ไม่ถามซ้ำจนกว่าจะปิดแท็บ (sessionStorage) */
export function skipSetupPrompt() {
  try { sessionStorage.setItem(PROMPT_SKIP_KEY, '1'); } catch (e) { /* ignore */ }
}

export function shouldPromptSetup() {
  if (state.configured) return false;
  try { return sessionStorage.getItem(PROMPT_SKIP_KEY) !== '1'; } catch (e) { return true; }
}
